import React, { useState, useEffect } from 'react';
import { Settings, Bell, Save, X, AlertCircle, Palette } from 'lucide-react';
import ThemeSwitcher from './ThemeSwitcher';
import { useTheme } from '../context/ThemeContext';

const defaultPreferences = () => ({
  notifications: {
    updates: localStorage.getItem('notifications.updates') === 'true',
  },
  chat: {
    autoSave: localStorage.getItem('chat.autoSave') !== 'false',
    showTimestamps: localStorage.getItem('chat.showTimestamps') === 'true',
  },
  theme: localStorage.getItem('app_theme') || 'light',
});

/**
 * Preferences Component
 * Allows users to customize their application preferences. Syncs with backend when apiClient is provided.
 */
const Preferences = ({ user, apiClient, onClose, onUpdate }) => {
  const { theme, setThemeByName } = useTheme();
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Track if theme has changed since mount to prevent overwriting user selection with stale backend data
  const initialThemeRef = React.useRef(theme);

  // Load preferences from API when modal opens (authenticated + apiClient)
  useEffect(() => {
    if (!apiClient || !user?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get('/auth/me/preferences');
        const remote = res?.data?.preferences;
        if (cancelled || !remote) return;
        const merged = {
          ...defaultPreferences(),
          ...remote,
          notifications: { ...defaultPreferences().notifications, ...(remote.notifications || {}) },
          chat: { ...defaultPreferences().chat, ...(remote.chat || {}) },
        };

        // Safest: if a local theme exists, it wins over backend to avoid bounce on first load
        const hasLocalTheme = localStorage.getItem('app_theme') !== null;
        // Only apply backend theme if no local theme is set and user hasn't manually changed it
        if (merged.theme && !hasLocalTheme && theme === initialThemeRef.current) {
          setThemeByName(merged.theme);
        }

        setPreferences(merged);
      } catch (_) {
        // Keep local defaults on error
      }
    })();
    return () => { cancelled = true; };
  }, [apiClient, user?.id, setThemeByName, theme]);

  const handleSave = async () => {
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const fullPrefs = { ...preferences, theme };
      // Save to localStorage for immediate UX and offline
      Object.keys(fullPrefs).forEach(key => {
        if (typeof fullPrefs[key] === 'object' && fullPrefs[key] !== null && !Array.isArray(fullPrefs[key])) {
          Object.keys(fullPrefs[key]).forEach(subKey => {
            localStorage.setItem(`${key}.${subKey}`, fullPrefs[key][subKey]);
          });
        } else if (typeof fullPrefs[key] !== 'object') {
          localStorage.setItem(key === 'theme' ? 'app_theme' : key, fullPrefs[key]);
        }
      });

      if (apiClient) {
        await apiClient.put('/auth/me/preferences', { preferences: fullPrefs });
      }

      // Notify in-app listeners (same-tab) without polling
      window.dispatchEvent(new CustomEvent('preferences:update', { detail: fullPrefs }));

      setSuccess('Preferences saved successfully!');
      setTimeout(() => {
        if (onUpdate) onUpdate(fullPrefs);
        if (onClose) onClose();
      }, 1000);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = (category, key, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const isDarkTheme = theme === 'dark';
  const isHighContrastTheme = theme === 'highContrast';
  const saveButtonStyle = loading
    ? (isDarkTheme
      ? { background: '#596584', color: '#ecf2ff', boxShadow: 'none' }
      : isHighContrastTheme
        ? { background: '#666666', color: '#ffffff', boxShadow: 'none' }
        : { background: '#cbd5e1', color: '#334155', boxShadow: 'none' })
    : { background: 'var(--accent-primary)', color: '#ffffff' };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 animate-in fade-in duration-200">
      <div className="preferences-modal bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] sm:max-h-[90vh] flex flex-col border border-gray-100 overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="preferences-modal-header px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Preferences</h2>
            <p className="text-xs sm:text-sm text-gray-500 mt-0.5 sm:mt-1 truncate">Customize your application experience</p>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center min-h-[44px] min-w-[44px] p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-xl transition-all shadow-sm border border-transparent hover:border-gray-200 flex-shrink-0"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-8 min-h-0">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start text-red-800">
              <AlertCircle className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-sm font-medium">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-start text-green-800">
              <AlertCircle className="h-5 w-5 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-sm font-medium">{success}</p>
            </div>
          )}

          <div className="space-y-6 sm:space-y-10">
            {/* Notifications Section */}
            <div>
              <div className="flex items-center space-x-2 mb-3 sm:mb-5">
                <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                  <Bell className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
              </div>

              <div className="bg-gray-50 border border-gray-100 rounded-xl p-1 space-y-1">
                <label className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-white rounded-lg shadow-sm border border-transparent hover:border-blue-100 transition-all cursor-pointer group">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">Product Updates</div>
                    <div className="text-xs text-gray-500 mt-0.5">Get notified about new features and updates</div>
                  </div>
                  <div className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.notifications.updates}
                      onChange={(e) => updatePreference('notifications', 'updates', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </div>
                </label>
              </div>
            </div>

            {/* Theme Settings Section */}
            <div>
              <div className="flex items-center space-x-2 mb-3 sm:mb-5">
                <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                  <Palette className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Theme</h3>
              </div>

              <div className="bg-gray-50 border border-gray-100 rounded-xl p-3 sm:p-4">
                <div className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">Select your preferred color theme</div>
                <ThemeSwitcher />
              </div>
            </div>

            {/* Chat Settings Section */}
            <div>
              <div className="flex items-center space-x-2 mb-3 sm:mb-5">
                <div className="p-2 bg-purple-50 rounded-lg text-purple-600">
                  <Settings className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Chat Settings</h3>
              </div>

              <div className="bg-gray-50 border border-gray-100 rounded-xl p-1 space-y-1">
                {/* Auto Save */}
                <label className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-white rounded-lg shadow-sm border border-transparent hover:border-purple-100 transition-all cursor-pointer group">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">Auto-save Conversations</div>
                    <div className="text-xs text-gray-500 mt-0.5">Automatically save your chat history</div>
                  </div>
                  <div className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.chat.autoSave}
                      onChange={(e) => updatePreference('chat', 'autoSave', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </div>
                </label>

                {/* Show Timestamps */}
                <label className="flex items-center justify-between gap-3 p-3 sm:p-4 bg-white rounded-lg shadow-sm border border-transparent hover:border-purple-100 transition-all cursor-pointer group">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">Show Timestamps</div>
                    <div className="text-xs text-gray-500 mt-0.5">Display time information for each message</div>
                  </div>
                  <div className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.chat.showTimestamps}
                      onChange={(e) => updatePreference('chat', 'showTimestamps', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </div>
                </label>
              </div>
            </div>

          </div>
        </div>

        {/* Footer */}
        <div className="preferences-modal-footer px-4 sm:px-8 py-4 sm:py-5 border-t border-gray-100 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-3 bg-gray-50/30 flex-shrink-0">
          <button
            onClick={onClose}
            className="preferences-cancel-btn px-5 py-3 sm:py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm min-h-[44px]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="preferences-save-btn px-5 py-3 sm:py-2.5 text-sm font-medium rounded-xl transition-all shadow-md hover:shadow-lg disabled:cursor-not-allowed flex items-center justify-center space-x-2 active:scale-95 min-h-[44px]"
            style={saveButtonStyle}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                <span>Saving...</span>
              </>
            ) : (
              <>
                <Save className="h-4 w-4" />
                <span>Save Preferences</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Preferences;
